import { ApiProvider } from '@issuemanager/api-client';
import {
  ErrorsByStatusesWithDefault,
  useAppErrorHandler,
  useEntitlementProblemNotifier,
} from '@issuemanager/apps/client/contexts';
import {
  useAppConfig,
  useJwtHighbondClient,
} from '@issuemanager/apps/client/hooks';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AxiosError } from 'axios';
import React, { useMemo } from 'react';
import {
  mutationErrorsByStatuses,
  queryErrorsByStatuses,
} from '../../contexts/app-error-handler-provider/http-error-messages';
import { environment } from '../../environments/environment';

interface Props {
  children?: React.ReactNode;
}

export default function ApiProviderWrapper({
  children,
}: Props): React.JSX.Element {
  const appConfig = useAppConfig();
  const highbondClient = useJwtHighbondClient();
  const appErrorHandler = useAppErrorHandler();
  const entitlementErrorNotifier = useEntitlementProblemNotifier();

  const handleAppErrorSet = (
    apiError: Error,
    clientErrorsByStatuses: Record<number, ErrorsByStatusesWithDefault>,
  ): void => {
    if (!(apiError instanceof AxiosError) || !apiError?.response?.status) {
      return;
    }

    const errorsByStatus = clientErrorsByStatuses[apiError.response?.status];

    if (!errorsByStatus) return;

    const errorType = apiError?.response?.data?.type?.toLowerCase();

    const existingErrorType = errorsByStatus.find(
      (errorByStatus) => errorByStatus.type.toLowerCase() === errorType,
    );

    const defaultErrorType = errorsByStatus.find(
      (errorByStatus) => errorByStatus.isDefault,
    );

    const clientError = existingErrorType ?? defaultErrorType;

    if (!clientError) {
      throw new Error('No matching error type and no default error type found');
    }

    appErrorHandler.setError({
      type: clientError.type,
      userInterface: clientError.userInterface,
    });
  };

  const handleGlobalQueryError = (error: Error): void => {
    handleAppErrorSet(error, queryErrorsByStatuses);
  };

  const handleGlobalMutationError = (error: Error): void => {
    handleAppErrorSet(error, mutationErrorsByStatuses);
  };

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchInterval: false,
            retry: 0,
            retryDelay: 500,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (!query.options.meta?.handleError) {
              handleGlobalQueryError(error);
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            handleGlobalMutationError(error);
          },
        }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      {environment.production ? null : (
        <ReactQueryDevtools
          client={queryClient}
          initialIsOpen={false}
          buttonPosition='top-right'
        />
      )}
      <ApiProvider
        highbondClient={highbondClient}
        appConfig={{
          refreshUrl: appConfig.tokenRefreshUrl,
          loginUrl: appConfig.loginUrl,
          apiUrl: appConfig.apiUrl,
        }}
        notifyEntitlementError={() => {
          if (entitlementErrorNotifier) entitlementErrorNotifier();
        }}
      >
        {children}
      </ApiProvider>
    </QueryClientProvider>
  );
}
